import * as React from 'react';
import { X, CheckCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { Button } from './button';

/**
 * Banner/Notification component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 202:434):
 * - Border Radius: 10px
 * - Padding: 10px 16px left, 12px right
 * - Shadow: subtle border shadow
 * - Icon: 40px with gradient and inner glow
 * - Variants: Success, Warning, Error, Info
 */
const bannerVariants = cva(
  'flex items-center gap-3 px-4 py-2.5 rounded-[10px] bg-white shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.1)] dark:bg-grey-900 dark:shadow-[0px_1px_1px_0px_rgba(255,255,255,0.05),0px_0px_0px_1px_rgba(255,255,255,0.1)]',
  {
    variants: {
      variant: {
        success: '',
        warning: '',
        error: '',
        info: '',
      },
    },
    defaultVariants: {
      variant: 'success',
    },
  }
);

const iconGradients = {
  success: 'from-green-400 to-green-700',
  warning: 'from-orange-300 to-orange-600',
  error: 'from-red-400 to-red-700',
  info: 'from-grey-400 to-grey-600',
};

const iconComponents = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  info: Info,
};

const BannerIcon = ({ variant }) => {
  const Icon = iconComponents[variant];
  return (
    <div className={cn(
      'w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-b shadow-[inset_0px_0px_0px_1px_rgba(0,0,0,0.1),inset_0px_-2px_4px_0px_rgba(255,255,255,0.5)]',
      iconGradients[variant]
    )}>
      <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
    </div>
  );
};

const Banner = React.forwardRef(
  (
    {
      className,
      variant = 'success',
      title,
      description,
      primaryAction,
      secondaryAction,
      onClose,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(bannerVariants({ variant }), className)}
        role="alert"
        {...props}
      >
        <BannerIcon variant={variant} />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-grey-950 dark:text-grey-50">
            {title}
          </p>
          {description && (
            <p className="text-sm text-grey-500">
              {description}
            </p>
          )}
        </div>

        {primaryAction && (
          <Button
            variant="default"
            size="sm"
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </Button>
        )}

        {secondaryAction && (
          <Button
            variant="secondary"
            size="sm"
            onClick={secondaryAction.onClick}
          >
            {secondaryAction.label}
          </Button>
        )}

        {onClose && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }
);
Banner.displayName = 'Banner';

export { Banner, BannerIcon, bannerVariants };
